import axios from 'axios'
import { authService, getAuthToken } from './authService'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000/api'

export const companyService = {
  // Basic User/Auth methods
  async getCurrentUser() {
    return authService.getCurrentUser()
  },

  async register(registrationData) {
    try {
      const response = await axios.post(`${API_BASE_URL}/register`, registrationData)

      if (response.data) {
        const { userId, companyId, hasCompletedRegistration, token, access_token } = response.data
        const authToken = token || access_token || response.data.data?.token || response.data.data?.access_token

        if (authToken) {
          const tokenData = { token: authToken, timestamp: Date.now() }
          sessionStorage.setItem("authToken", JSON.stringify(tokenData))
        }

        if (userId) sessionStorage.setItem('userId', JSON.stringify(userId))
        if (companyId) sessionStorage.setItem('companyId', JSON.stringify(companyId))

        if (hasCompletedRegistration !== undefined) {
          sessionStorage.setItem('hasCompletedRegistration', JSON.stringify(hasCompletedRegistration))
        }

        if (authToken && !response.data.token && !response.data.access_token) {
          response.data.token = authToken
        }
      }
      return response.data
    } catch (error) {
      console.error('❌ Company registration error:', error)
      throw new Error(error.response?.data?.message || error.message || 'Registration failed')
    }
  },

  // Step 1: Legal Details
  // Step 1: Legal Details - POST /company-register
  async saveLegalDetails(companyData) {
    try {
      const user = await authService.getCurrentUser()
      const userId = user?.id || user?.user_id
      const token = getAuthToken()

      if (!userId) throw new Error('User not authenticated')

      const response = await axios.post(`${API_BASE_URL}/company-register`, {
        userId: userId,
        ...companyData,
        step: 'company-register',
        timestamp: new Date().toISOString(),
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      return response.data
    } catch (error) {
      console.error('❌ Error saving legal details:', error)
      throw error
    }
  },

  // GET /company-register/{user_id}
  async getCompanyRegisterData(userId) {
    try {
      const token = getAuthToken()
      if (!token) throw new Error('No authentication token')
      if (!userId) throw new Error('User ID required')

      const url = `${API_BASE_URL}/company-register/${userId}`

      const response = await axios.get(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      return response.data
    } catch (error) {
      console.error('Error fetching register data:', error)
      throw error
    }
  },

  // Step 2: Profile Building
  // Step 2: Profile Building - POST /company-profile
  async saveProfileDetails(companyData) {
    try {
      const user = await authService.getCurrentUser()
      const userId = user?.id || user?.user_id
      const token = getAuthToken()

      if (!userId) throw new Error('User not authenticated')

      const response = await axios.post(`${API_BASE_URL}/company-profile`, {
        user_id: userId,
        ...companyData,
        step: 'company-profile',
        timestamp: new Date().toISOString(),
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      return response.data
    } catch (error) {
      console.error('❌ Error saving profile details:', error)
      throw error
    }
  },

  // GET /company-profile/{user_id}
  async getCompanyProfileData(userId) {
    try {
      const token = getAuthToken()
      if (!token) throw new Error('No authentication token')
      if (!userId) throw new Error('User ID required')

      const url = `${API_BASE_URL}/company-profile/${userId}`

      const response = await axios.get(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      return response.data
    } catch (error) {
      console.error('Error fetching profile data:', error)
      throw error
    }
  },

  // Step 3: Subscription (Processing Payment)
  // Step 3: Subscription - POST /company-subscription
  async saveSubscription(subscriptionData) {
    try {
      const token = getAuthToken()
      if (!token) throw new Error('User not authenticated')

      const response = await axios.post(`${API_BASE_URL}/company-subscription`, subscriptionData, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.data?.subscription_id) {
        sessionStorage.setItem('companySubscription', JSON.stringify({
          subscription_id: response.data.subscription_id,
          plan: subscriptionData.plan_name,
          status: 'active'
        }))
      }

      return response.data
    } catch (error) {
      console.error('Subscription error:', error)
      throw error
    }
  },

  // GET /company-subscription
  // GET /company-subscription
  async getSubscription(userId) {
    try {
      const token = getAuthToken()
      if (!token) throw new Error('User not authenticated')
      if (!userId) {
        // Try to get from session if not passed
        try {
          const user = await this.getCurrentUser();
          userId = user?.id || user?.user_id;
        } catch (e) {
          console.warn("Could not fetch user for subscription get", e);
        }
      }

      const response = await axios.get(`${API_BASE_URL}/company-subscription`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      return response.data
    } catch (error) {
      console.error('Get subscription error:', error)
      throw error
    }
  },

  // Alias for backward compatibility if needed
  async processSubscriptionPayment(data) { return this.saveSubscription(data) },

  // Alias for getCompanyProfile using generic ID (mapping to new getCompanyProfileData)
  async getCompanyProfile(userId) {
    return this.getCompanyProfileData(userId)
  },

  // Step 4: Documents
  async uploadDocument(documentData) {
    try {
      const token = getAuthToken()
      // documentData should be FormData
      const response = await axios.post(`${API_BASE_URL}/documents/upload`, documentData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          // Content-Type is auto-set for FormData
        }
      })
      return response.data
    } catch (error) {
      console.error('Error uploading document:', error)
      throw error
    }
  },

  // Create a talent request (client -> server)
  // Maps to POST /api/company/resource-requests
  async createResourceRequest(requestData) {
    try {
      const user = await authService.getCurrentUser()
      const userId = user?.id || user?.user_id
      // Try to get companyId from session, fallback to user object
      const companyId = JSON.parse(sessionStorage.getItem('companyId') || 'null') || user?.company_id || user?.companyId

      const token = getAuthToken()
      // Ensure specific fields are present as per API requirements
      const payload = {
        company_id: companyId,
        user_id: userId,
        ...requestData
      }

      const response = await axios.post(`${API_BASE_URL}/company/resource-requests`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      return response.data
    } catch (error) {
      console.error('Error creating resource request:', error)
      throw error
    }
  },

  // Backward compatibility alias using the new generic method
  async createTalentRequest(requestData) {
    return this.createResourceRequest(requestData)
  },

  // Update BGV status for current user using client-side API
  async updateUserBGVStatus(bgvStatus, rejectionReason = null) {
    try {
      // Only allow "uploaded" status for client-side updates
      if (bgvStatus !== 'uploaded') {
        throw new Error('Invalid BGV status. Only "uploaded" status is allowed for client-side updates.')
      }

      const user = await authService.getCurrentUser()
      const userId = user?.id || user?.user_id
      const token = getAuthToken()

      if (!userId) {
        throw new Error('User not authenticated')
      }

      if (!token) {
        throw new Error('Authentication required')
      }

      const requestData = {
        bgvStatus: bgvStatus
      }

      // Add rejection reason if provided
      if (rejectionReason) {
        requestData.reason = rejectionReason
      }

      const response = await axios.patch(
        `${API_BASE_URL}/bgv/${userId}`,
        requestData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to update BGV status')
      }

      return response.data
    } catch (error) {
      console.error('User BGV status update error:', error)
      throw error
    }
  },


  // General / Enums
  async getEnum(enumName) {
    try {
      const token = getAuthToken()
      const config = token ? { headers: { 'Authorization': `Bearer ${token}` } } : {}
      const response = await axios.get(`${API_BASE_URL}/?enum=${enumName}`, config)
      return response.data
    } catch (error) {
      console.error(`Error fetching enum ${enumName}:`, error)
      return { success: false, data: [] }
    }
  },

  // Existing methods that might still be useful (aliases or specific implementations)
  async getCompanyDashboard(userId) {
    try {
      const token = getAuthToken()
      const response = await axios.get(`${API_BASE_URL}/company/dashboard/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      return response.data
    } catch (error) {
      console.error('Get company user error:', error)
      throw error
    }
  },




  // Fetch a single bench resource by ID: /bench-resource/{resource_id}
  async getBenchResource(resourceId) {
    try {
      const token = getAuthToken()
      if (!token) {
        throw new Error('Authentication required')
      }

      const endpoint = `${API_BASE_URL}/bench-resource/${resourceId}`
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const err = await response.json().catch(() => ({}))
        const msg = err.message || 'Failed to fetch bench resource'
        throw new Error(msg)
      }

      const data = await response.json()

      // Cache the fetched bench resource inside vendorResources (merge/replace by id)
      if (data && data.data) {
        try {
          const existing = JSON.parse(sessionStorage.getItem('vendorResources') || '[]')
          const idx = existing.findIndex(r => r.id === data.data.id)
          if (idx >= 0) {
            existing[idx] = data.data
          } else {
            existing.push(data.data)
          }
          sessionStorage.setItem('vendorResources', JSON.stringify(existing))
        } catch (e) {
          // If caching fails, don't block the main response
          console.warn('Failed to cache bench resource:', e)
        }
      }

      return data
    } catch (error) {
      console.error('getBenchResource error:', error)
      throw new Error(error.message || 'Failed to fetch bench resource')
    }
  },

  // List bench resources for company (search / listing)
  // Handles pagination and filters via query params
  async getBenchResources(params = {}) {
    try {
      const token = getAuthToken()
      const config = {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        params
      }

      const url = `${API_BASE_URL}/company/list-bench-resources`
      const response = await axios.get(url, config)

      const respData = response.data || {}
      const data = respData.data || {}

      // Handle both array (simple list) and object with resources key (pagination) response structures
      const rawResources = Array.isArray(data) ? data : (data.resources || [])
      const totalCount = data.total_count || data.totalCount || rawResources.length

      const resources = rawResources.map((r) => ({
        id: r.id,
        vendor_id: r.vendorId || r.vendor_id,
        user_id: r.userId || r.user_id,
        first_name: r.first_name || r.firstName,
        last_name: r.last_name || r.lastName,
        name: r.name,
        email: r.email,
        phone: r.phone,
        experience: r.total_experience || r.totalExperience || r.relevant_experience || r.experience || null,
        relevant_experience: r.relevant_experience || r.relevantExperience || null,
        location: r.location,
        designation: r.designation,
        department: r.department,
        availability: r.availability,
        cost_per_hour: r.costperHour || r.cost_per_hour || r.costPerHour || null,
        primary_skills: r.primarySkills || r.primary_skills || [],
        secondary_skills: r.secondarySkills || r.secondary_skills || [],
        technologies: r.technologies || [],
        rating: r.rating,
        vendor_name: r.vendorName || r.vendor_name || null,
        notice_period: r.notice_period || r.noticePeriod || null,
        current_project: r.current_project || r.currentProject || null,
        joining_date: r.joining_date || r.joiningDate || null,
        summary_text: r.summary_text || null
      }))

      const pageSize = params.page_size || 10
      const totalPages = data.total_pages || Math.ceil(totalCount / pageSize) || 1

      return {
        success: respData.success !== undefined ? respData.success : true,
        data: {
          resources,
          total_count: totalCount,
          total_pages: Number(totalPages) || 1,
          current_page: Number(data.current_page) || Number(params.page) || 1
        },
        message: respData.message || ''
      }
    } catch (error) {
      console.error('Error fetching bench resources:', error)
      return { success: false, data: { resources: [], total_count: 0 }, message: error.message || 'Failed to fetch resources' }
    }
  },

  // Get full details of a resource
  // Endpoint: GET /api/company/resource-full-view/{resource_id}
  async getResourceFullView(resourceId) {
    try {
      const token = getAuthToken()
      if (!token) throw new Error('Authentication required')

      const response = await axios.get(`${API_BASE_URL}/company/resource-full-view/${resourceId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      return response.data
    } catch (error) {
      console.error('Error fetching resource full view:', error)
      throw error
    }
  },

  // Fetch resource filters / filtered resources from server
  // Endpoint: GET /company/resources-filter
  // Accepts query params for filtering; returns normalized resources similar to getBenchResources
  async getResourceFilters(params = {}) {
    try {
      const token = getAuthToken()
      const config = {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        params
      }

      const url = `${API_BASE_URL}/company/resources-filter`
      const response = await axios.get(url, config)

      const respData = response.data || {}
      const data = respData.data || {}

      const resources = (data.resources || []).map((r) => ({
        id: r.id,
        vendor_id: r.vendorId || r.vendor_id || r.vendor_id,
        user_id: r.userId || r.user_id,
        first_name: r.first_name || r.firstName,
        last_name: r.last_name || r.lastName,
        name: r.name,
        email: r.email,
        phone: r.phone,
        experience: r.total_experience || r.totalExperience || r.relevant_experience || r.experience || null,
        relevant_experience: r.relevant_experience || r.relevantExperience || null,
        location: r.location,
        designation: r.designation,
        department: r.department,
        availability: r.availability,
        cost_per_hour: r.costperHour || r.cost_per_hour || r.costPerHour || null,
        primary_skills: r.primarySkills || r.primary_skills || [],
        secondary_skills: r.secondarySkills || r.secondary_skills || [],
        technologies: r.technologies || [],
        rating: r.rating,
        vendor_name: r.vendorName || r.vendor_name || null,
        notice_period: r.notice_period || r.noticePeriod || null,
        current_project: r.current_project || r.currentProject || null,
        joining_date: r.joining_date || r.joiningDate || null,
      }))

      return {
        success: respData.success !== undefined ? respData.success : true,
        data: {
          resources,
          total_count: data.total_count || data.totalCount || resources.length
        },
        message: respData.message || ''
      }
    } catch (error) {
      console.error('Error fetching resource filters:', error)
      return { success: false, data: { resources: [], total_count: 0 }, message: error.message || 'Failed to fetch resources' }
    }
  },

  // Aliases for backward compatibility
  // Aliases
  async saveCompanyDetails(data) { return this.saveLegalDetails(data) },
  async saveCompanyProfile(data) { return this.saveProfileDetails(data) },
  async processCompanySubscription(data) { return this.saveSubscription(data) }
}
